import { useCallback, useMemo } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  Button,
} from '@coreai/fluentui-react';
import {
  AddRegular,
  DeleteRegular,
  OrganizationHorizontalRegular,
  ServerRegular,
  WrenchRegular,
} from '@fluentui/react-icons';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useGetConnectorInfo } from '@/contexts';
import { isAgentAction } from '@/types/workflowDefinition';
import { resolveToolDisplay } from '@/components/WorkflowDesigner/nodes/toolDisplay';
import type { Action, AgentTool } from '@/types/workflowDefinition';

/**
 * Window event that opens the canvas tool-type chooser anchored at the
 * given element. WorkflowDesigner owns the chooser; ParameterList lives in
 * a sibling subtree, so we bridge with a CustomEvent instead of prop-
 * drilling through the node editor store.
 */
export const AGENT_ADD_TOOL_EVENT = 'agent-add-tool';

export interface AgentAddToolEventDetail {
  agentName: string;
  anchor: HTMLElement;
}

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  header: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  empty: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    cursor: 'pointer',
    border: `1px solid ${tokens.colorTransparentStroke}`,
    transitionProperty: 'background-color, border-color',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      border: `1px solid ${tokens.colorNeutralStroke1Hover}`,
    },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '2px',
    },
    '@media (prefers-reduced-motion: reduce)': {
      transitionDuration: '0.01ms',
    },
  },
  iconContainer: {
    width: '24px',
    height: '24px',
    minWidth: '24px',
    borderRadius: tokens.borderRadiusSmall,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground4,
    overflow: 'hidden',
  },
  iconContainerMcp: {
    backgroundColor: tokens.colorPaletteMarigoldBackground2,
  },
  connectorIcon: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  defaultIcon: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  defaultIconMcp: {
    color: tokens.colorPaletteMarigoldForeground2,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  name: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  description: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  deleteButton: {
    flexShrink: 0,
  },
  addButton: {
    alignSelf: 'flex-start',
  },
});

const humanizeToolName = (name: string): string => name.replace(/_/g, ' ');

/** Pull the inner action's connection reference name out of inputs.host. */
const extractConnectionName = (action: Action | undefined): string | undefined => {
  const host = (action?.inputs as { host?: { connection?: { referenceName?: string } } } | undefined)?.host;
  return host?.connection?.referenceName;
};

interface ToolEntry {
  name: string;
  description: string;
  displayName: string;
  iconUri?: string | undefined;
  brandColor?: string | undefined;
  /** Tool wrapper type — drives MCP-specific affordances. */
  toolKind?: string | undefined;
  /** Inner action type (e.g. 'Workflow', 'Compose') — drives fallback icon. */
  innerActionType?: string | undefined;
}

const getFirstInnerAction = (
  tool: AgentTool,
): { name: string | undefined; action: Action | undefined } => {
  const actions = tool.actions ?? {};
  const name = Object.keys(actions)[0];
  return { name, action: name ? actions[name] : undefined };
};

export interface AgentToolsSectionProps {
  agentName: string;
  readOnly?: boolean;
}

/**
 * Compact "Tools" section rendered inside the agent's parameters tab.
 * Mirrors the canvas-level tool affordances (configure / add / delete) so
 * the panel and the floating canvas node stay in lockstep.
 */
export const AgentToolsSection = ({ agentName, readOnly = false }: AgentToolsSectionProps) => {
  const styles = useStyles();
  const getConnectorInfo = useGetConnectorInfo();

  const tools = useWorkflowStore((s) => {
    const action = s.getAction(agentName);
    if (!action || !isAgentAction(action)) return undefined;
    return action.tools;
  });

  const selectTool = useWorkflowStore((s) => s.selectTool);
  const deleteToolFromAgent = useWorkflowStore((s) => s.deleteToolFromAgent);

  const toolEntries = useMemo<ToolEntry[]>(() => {
    if (!tools) return [];
    return Object.entries(tools).map(([name, tool]: [string, AgentTool]) => {
      const { name: innerActionName, action: innerAction } = getFirstInnerAction(tool);
      const display = resolveToolDisplay(
        {
          connectionName: extractConnectionName(innerAction),
          actionName: innerActionName,
          actionType: innerAction?.type,
        },
        getConnectorInfo,
      );
      return {
        name,
        description: tool.description ?? '',
        displayName: display.displayName ?? humanizeToolName(name),
        iconUri: display.iconUri,
        brandColor: display.brandColor,
        toolKind: tool.type,
        innerActionType: innerAction?.type,
      };
    });
  }, [tools, getConnectorInfo]);

  const handleSelect = useCallback(
    (toolName: string) => {
      // The node editor's NodeEditor.tsx routes the tool to the correct
      // editor surface (Parameters tab for action tools; MCP allowed-tools
      // tab for MCP tools). We just need to mark the tool as selected.
      selectTool(agentName, toolName);
    },
    [agentName, selectTool],
  );

  const handleDelete = useCallback(
    async (toolName: string) => {
      const editorState = useNodeEditorStore.getState();
      if (editorState.isDirty) {
        const result = await editorState.applyChanges();
        if (!result.success) return;
      }

      deleteToolFromAgent(agentName, toolName);

      // Keep the open agent editor synchronized with the store. Otherwise a
      // pending auto-save can serialize its stale nodeData and re-add the tool.
      const updatedAgent = useWorkflowStore.getState().getAction(agentName);
      const currentEditorState = useNodeEditorStore.getState();
      const actionName = agentName.includes('/') ? agentName.split('/').pop()! : agentName;
      if (
        updatedAgent &&
        isAgentAction(updatedAgent) &&
        currentEditorState.isOpen &&
        currentEditorState.node?.type === 'action' &&
        currentEditorState.node.name === actionName
      ) {
        currentEditorState.refreshFromWorkflow(updatedAgent);
      }
    },
    [agentName, deleteToolFromAgent],
  );

  const handleAddClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const anchor = e.currentTarget;
      window.dispatchEvent(
        new CustomEvent<AgentAddToolEventDetail>(AGENT_ADD_TOOL_EVENT, {
          detail: { agentName, anchor },
        }),
      );
    },
    [agentName],
  );

  /** Pick the leftmost rendered glyph for a tool when no manifest icon hits. */
  const renderFallbackIcon = (tool: ToolEntry) => {
    if (tool.toolKind === 'McpClientTool') {
      return <ServerRegular className={mergeClasses(styles.defaultIcon, styles.defaultIconMcp)} />;
    }
    if (tool.innerActionType === 'Workflow') {
      return <OrganizationHorizontalRegular className={styles.defaultIcon} />;
    }
    return <WrenchRegular className={styles.defaultIcon} />;
  };

  return (
    <div className={styles.section} data-testid="agent-tools-section">
      <Text className={styles.header}>Tools</Text>
      {toolEntries.length > 0 ? (
        <div className={styles.list}>
          {toolEntries.map((tool) => (
            <div
              key={tool.name}
              className={styles.item}
              role="button"
              tabIndex={0}
              onClick={() => handleSelect(tool.name)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelect(tool.name);
                }
              }}
              data-testid={`agent-tool-${tool.name}`}
            >
              <div
                className={mergeClasses(
                  styles.iconContainer,
                  tool.toolKind === 'McpClientTool' && !tool.iconUri ? styles.iconContainerMcp : undefined,
                )}
                style={
                  tool.toolKind !== 'McpClientTool' && tool.brandColor
                    ? { backgroundColor: tool.brandColor }
                    : undefined
                }
              >
                {tool.iconUri ? (
                  <img src={tool.iconUri} alt="" className={styles.connectorIcon} />
                ) : (
                  renderFallbackIcon(tool)
                )}
              </div>
              <div className={styles.content}>
                <Text className={styles.name}>{tool.displayName}</Text>
                {tool.description && (
                  <Text className={styles.description}>{tool.description}</Text>
                )}
              </div>
              {!readOnly && (
                <Button
                  className={styles.deleteButton}
                  appearance="transparent"
                  size="small"
                  icon={<DeleteRegular />}
                  aria-label={`Delete ${tool.displayName}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleDelete(tool.name);
                  }}
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        <Text className={styles.empty}>No tools configured.</Text>
      )}
      {!readOnly && (
        <Button
          className={styles.addButton}
          appearance="secondary"
          size="small"
          icon={<AddRegular />}
          onClick={handleAddClick}
          data-testid="agent-add-tool-button"
        >
          Add tool
        </Button>
      )}
    </div>
  );
};

AgentToolsSection.displayName = 'AgentToolsSection';
